import { useState, useEffect, useReducer } from 'react'

type Action =
  | { type: 'request' }
  | {
      type: 'success'
      results: Array<Record<string, unknown>> | Record<string, unknown>
    }
  | { type: 'failure'; error: string }

type State = {
  data?: Array<Record<string, unknown>> | Record<string, unknown>
  loading: boolean
  error?: string
}
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'request':
      return { loading: true, error: '' }
    case 'success':
      return { loading: false, data: action.results, error: '' }
    case 'failure':
      return { loading: false, error: action.error }
  }
}

interface FetchType {
  data: any
  error?: Error
  loading: boolean
  fetch: (url: string, options?: any) => void
  setLoading: (loading: boolean) => void
}
// fetch data from api, optionally using options
const useFetch = (url: string, options?: any): FetchType => {
  const [{ data, loading, error }, dispatch] = useReducer(reducer, {
    error: '',
    loading: false
  })
  async function Asyncfetch(url: string, options: RequestInit = {}) {
    dispatch({ type: 'request' })
    try {
      const response = await fetch(url, options)
      if (response?.ok && response?.status !== 404) {
        const contentType = response.headers.get('content-type')
        const resData =
          contentType === contentType && contentType?.includes('json')
            ? await response.json()
            : contentType?.includes('text')
            ? await response.text()
            : await response.blob()
        dispatch(
          !!resData
            ? { type: 'success', results: resData }
            : { type: 'failure', error: 'No data available' }
        )
      } else {
        dispatch({ type: 'failure', error: 'No data available' })
      }
    } catch (error) {
      dispatch({
        type: 'failure',
        error: (error as Error)?.message || 'No data available'
      })
    }
  }

  useEffect(() => {
    if (url) {
      Asyncfetch(url, options)
    }
  }, [url, options])

  return {
    data: data,
    error: error ? new Error(error) : undefined,
    loading,
    fetch: Asyncfetch,
    setLoading: (loading: boolean) => {}
  }
}

// get size of element
const getSize = (el: HTMLElement) => {
  if (!el) return {}
  return {
    width:
      el.offsetWidth >= 35 && el.offsetWidth < 600
        ? el.offsetWidth - 35
        : el.offsetWidth,
    height: el.offsetHeight
  }
}
const useComponentSize = (ref) => {
  const [componentSize, setComponentSize] = useState(getSize(ref?.current))

  // eslint-disable-next-line react-hooks/exhaustive-deps
  function handleResize() {
    if (ref && ref?.current) {
      setComponentSize(getSize(ref?.current))
    }
  }

  useEffect(() => {
    handleResize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return componentSize
}

export { useFetch, useComponentSize }
